import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import { type FC } from 'react';
import { ClockGrayIcon, TaskOverviewIcons } from '../dashboard-icon';
import { RadialChartDocumentation } from './radial-charts/radial-chart-documentation';
import { RadialChartOther } from './radial-charts/radial-chart-other';
import { RadialChartTask } from './radial-charts/radial-chart-task';

interface OverviewDoneTasksProps {
  dataRes: any;
  showTaskDetails: boolean;
  index: number;
}

function getTotalTasksCount(data: any) {
  let i = 0;
  data?.map((item: any) => {
    i += item.totalCount;
  });
  return i;
}

function getCompletedTasksCount(data: any) {
  let i = 0;
  data?.map((item: any) => {
    i += item.completedCount;
  });
  return i;
}

function getCompletedDocsCount(data: any) {
  let i = 0;
  data?.map((item: any) => {
    i += item.completedCount;
  });
  return i;
}

function getTotalDocsCount(data: any) {
  let i = 0;
  data?.map((item: any) => {
    i += item.totalCount;
  });
  return i;
}

function getDocumentInfoObj(data: any, formType: string) {
  const filteredData = data.filter((item: any) => item.formType?.includes(formType));
  const completedCount = filteredData.reduce(
    (acc: number, item: any) => acc + item.completedCount,
    0
  );
  const totalCount = filteredData.reduce((acc: number, item: any) => acc + item.totalCount, 0);
  return {
    completedCount,
    totalCount,
  };
}

function getTherapyInfoObj(data: any, therapyType: string) {
  return data?.find((item: any) => item.tasksType === therapyType);
}

function getTaskTypeInfoObj(data: any, taskType: string) {
  return data?.find((item: any) => item.tasksType === taskType);
}

function getRemainingHrs(data: any) {
  let completedMinutes = 0;
  let totalMinutes = 0;
  
  data?.map((task: any) => {
    completedMinutes += task.completedMinutes;
    totalMinutes += task.totalMinutes;
  });

  const hours = Math.floor((totalMinutes - completedMinutes) / 60);
  const minutes = (totalMinutes - completedMinutes) % 60;

  return `${hours}:${minutes.toString().padStart(2, '0')} hrs remaining`;
}

export const OverviewDoneTasks: FC<OverviewDoneTasksProps> = (props) => {
  const { dataRes, showTaskDetails, index } = props;

  return (
    <Box
      sx={{
        borderRadius: '0px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 'none',
        borderRight: showTaskDetails ? 'none' : '1px solid #F2F4F7',
      }}
    >
      <Box
        // pb={1}

        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          marginTop: showTaskDetails ? '0px' : '20px',
          borderRight: showTaskDetails ? '1px solid #F2F4F7' : 'none',
          borderBottom: showTaskDetails ? '1px solid #F2F4F7' : 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: '40%',
          }}
        >
          {index === 0 ? (
            <RadialChartTask
              dataRes={(getCompletedTasksCount(dataRes) / getTotalTasksCount(dataRes)) * 100}
            />
          ) : index === 1 ? (
            <RadialChartDocumentation
              dataRes={(getCompletedDocsCount(dataRes) / getTotalDocsCount(dataRes)) * 100}
            />
          ) : index === 2 ? (
            <RadialChartOther
              dataRes={(getCompletedTasksCount(dataRes) / getTotalTasksCount(dataRes)) * 100}
            />
          ) : null}
        </Box>
        <Box sx={{ flexGrow: 1, marginTop: '10px', display: 'flex', flexDirection: 'column' }}>
          <Typography
            color="text.secondary"
            variant="body2"
          >
            {index === 0 ? 'Therapy Sessions' : index === 1 ? 'Pending docs' : 'Others'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <Typography
              color="text.primary"
              variant={showTaskDetails && !dataRes?.isSingleOpen ? 'h5' : 'h4'}
            >
              {index === 0
                ? getCompletedTasksCount(dataRes)
                : index === 1
                ? getCompletedDocsCount(dataRes)
                : getCompletedTasksCount(dataRes)}
            </Typography>

            <Typography
              color="text.primary"
              variant="h6"
            >
              /
              {index === 0
                ? getTotalTasksCount(dataRes)
                : index === 1
                ? getTotalDocsCount(dataRes)
                : getTotalTasksCount(dataRes)}
            </Typography>
          </Box>
        </Box>
      </Box>
      {!showTaskDetails && (
        <Box
          sx={{
            display: 'flex',
            marginTop: '15px',
            gap: '5px',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <ClockGrayIcon />
          <Typography
            color="text.secondary"
            variant="body2"
          >
            {index === 0
              ? getRemainingHrs(dataRes)
              : index === 1
              ? 'N/A'
              : getRemainingHrs(dataRes)}
          </Typography>
        </Box>
      )}
      {showTaskDetails && (
        <Box
          sx={{
            // borderLeft: '1px solid #F2F4F7',
            borderRight: '1px solid #F2F4F7',
            padding: 0.5,
            gap: index !== 1 ? '14px' : '4px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              width: '8px',
              display: 'none',
            },
          }}
        >
          {
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  px: 0.8,
                  py: 0.5,
                }}
                // onClick={() => handleSingleTask(dataRes)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TaskOverviewIcons
                    iconType={
                      index === 0
                        ? 'Individual therapy'
                        : index === 1
                        ? 'Daily Session Note'
                        : 'Screenings'
                    }
                  />
                  <Typography
                    sx={{ color: '#384250', marginLeft: '5px' }}
                    variant="caption"
                    noWrap
                  >
                    {index === 0 &&
                      `${
                        getTherapyInfoObj(dataRes, 'THERAPY')?.completedCount
                          ? getTherapyInfoObj(dataRes, 'THERAPY')?.completedCount
                          : 0
                      }/${
                        getTherapyInfoObj(dataRes, 'THERAPY')?.totalCount
                          ? getTherapyInfoObj(dataRes, 'THERAPY')?.totalCount
                          : 0
                      }`}
                    {index === 1 &&
                      `${
                        getDocumentInfoObj(dataRes, 'EVALUATION').completedCount
                          ? getDocumentInfoObj(dataRes, 'EVALUATION').completedCount
                          : 0
                      }/${
                        getDocumentInfoObj(dataRes, 'EVALUATION').totalCount
                          ? getDocumentInfoObj(dataRes, 'EVALUATION').totalCount
                          : 0
                      }`}
                    {index === 2 &&
                      `${
                        getTaskTypeInfoObj(dataRes, 'SCREENINGS')?.completedCount
                          ? getTaskTypeInfoObj(dataRes, 'SCREENINGS')?.completedCount
                          : 0
                      }/${
                        getTaskTypeInfoObj(dataRes, 'SCREENINGS')?.totalCount
                          ? getTaskTypeInfoObj(dataRes, 'SCREENINGS')?.totalCount
                          : 0
                      } `}
                  </Typography>
                  <Typography
                    sx={{ color: '#384250', marginLeft: '5px', marginRight: '5px' }}
                    variant="caption"
                    noWrap
                  >
                    {index === 0 ? `Individual therapy` : index === 1 ? `Evaluations` : 'Screening'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                  <Typography
                    sx={{ color: '#9E9E9E' }}
                    variant="caption"
                    noWrap
                  >
                    {index === 0 &&
                      `${
                        getTherapyInfoObj(dataRes, 'THERAPY')?.completedMinutes
                          ? getTherapyInfoObj(dataRes, 'THERAPY')?.completedMinutes
                          : 0
                      }/${
                        getTherapyInfoObj(dataRes, 'THERAPY')?.totalMinutes
                          ? getTherapyInfoObj(dataRes, 'THERAPY')?.totalMinutes
                          : 0
                      } mins `}
                    {index === 2 &&
                      `${
                        getTaskTypeInfoObj(dataRes, 'SCREENINGS')?.completedMinutes
                          ? getTaskTypeInfoObj(dataRes, 'SCREENINGS')?.completedMinutes
                          : 0
                      }/${
                        getTaskTypeInfoObj(dataRes, 'SCREENINGS')?.totalMinutes
                          ? getTaskTypeInfoObj(dataRes, 'SCREENINGS')?.totalMinutes
                          : 0
                      } mins `}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  px: 0.8,
                  py: 0.5,
                }}
                // onClick={() => handleSingleTask(dataRes)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TaskOverviewIcons
                    iconType={
                      index === 0
                        ? 'Concurrent therapy'
                        : index === 1
                        ? 'Recertification'
                        : 'Insurance call'
                    }
                  />
                  <Typography
                    sx={{ color: '#384250', marginLeft: '5px' }}
                    variant="caption"
                    noWrap
                  >
                    {index === 0 &&
                      `${
                        getTherapyInfoObj(dataRes, 'CONCURRENT')?.completedCount
                          ? getTherapyInfoObj(dataRes, 'CONCURRENT')?.completedCount
                          : 0
                      }/${
                        getTherapyInfoObj(dataRes, 'CONCURRENT')?.totalCount
                          ? getTherapyInfoObj(dataRes, 'CONCURRENT')?.totalCount
                          : 0
                      } `}
                    {index === 1 &&
                      `${
                        getDocumentInfoObj(dataRes, 'RECERTIFICATION').completedCount
                          ? getDocumentInfoObj(dataRes, 'RECERTIFICATION').completedCount
                          : 0
                      }/${
                        getDocumentInfoObj(dataRes, 'RECERTIFICATION').totalCount
                          ? getDocumentInfoObj(dataRes, 'RECERTIFICATION').totalCount
                          : 0
                      }`}
                    {index === 2 &&
                      `${
                        getTaskTypeInfoObj(dataRes, 'INSURANCE_CALL')?.completedCount
                          ? getTaskTypeInfoObj(dataRes, 'INSURANCE_CALL')?.completedCount
                          : 0
                      }/${
                        getTaskTypeInfoObj(dataRes, 'INSURANCE_CALL')?.totalCount
                          ? getTaskTypeInfoObj(dataRes, 'INSURANCE_CALL')?.totalCount
                          : 0
                      } `}
                  </Typography>
                  <Typography
                    sx={{ color: '#384250', marginLeft: '5px', marginRight: '5px' }}
                    variant="caption"
                    noWrap
                  >
                    {index === 0
                      ? `Concurrent therapy`
                      : index === 1
                      ? `Recertifications`
                      : `Insurance Call`}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2, px: 0.5 }}>
                  <Typography
                    sx={{ color: '#9E9E9E' }}
                    variant="caption"
                    noWrap
                  >
                    {index === 0 &&
                      `${
                        getTherapyInfoObj(dataRes, 'CONCURRENT')?.completedMinutes
                          ? getTherapyInfoObj(dataRes, 'CONCURRENT')?.completedMinutes
                          : 0
                      }/${
                        getTherapyInfoObj(dataRes, 'CONCURRENT')?.totalMinutes
                          ? getTherapyInfoObj(dataRes, 'CONCURRENT')?.totalMinutes
                          : 0
                      } `}
                    {index === 2 &&
                      `${
                        getTaskTypeInfoObj(dataRes, 'INSURANCE_CALL')?.completedMinutes
                          ? getTaskTypeInfoObj(dataRes, 'INSURANCE_CALL')?.completedMinutes
                          : 0
                      }/${
                        getTaskTypeInfoObj(dataRes, 'INSURANCE_CALL')?.totalMinutes
                          ? getTaskTypeInfoObj(dataRes, 'INSURANCE_CALL')?.totalMinutes
                          : 0
                      } `}
                    {index != 1 && 'mins'}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  px: 0.8,
                  py: 0.5,
                }}
                // onClick={() => handleSingleTask(dataRes)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {index === 0 && <TaskOverviewIcons iconType={'Group therapy'} />}
                  {index === 1 && <TaskOverviewIcons iconType={'Discharge'} />}

                  <Typography
                    sx={{ color: '#384250', marginLeft: '5px' }}
                    variant="caption"
                    noWrap
                  >
                    {index === 0 &&
                      `${
                        getTherapyInfoObj(dataRes, 'GROUP_THERAPY')?.completedCount
                          ? getTherapyInfoObj(dataRes, 'GROUP_THERAPY')?.completedCount
                          : 0
                      }/${
                        getTherapyInfoObj(dataRes, 'GROUP_THERAPY')?.totalCount
                          ? getTherapyInfoObj(dataRes, 'GROUP_THERAPY')?.totalCount
                          : 0
                      } `}
                    {index === 1 &&
                      `${
                        getDocumentInfoObj(dataRes, 'DISCHARGE').completedCount
                          ? getDocumentInfoObj(dataRes, 'DISCHARGE').completedCount
                          : 0
                      }/${
                        getDocumentInfoObj(dataRes, 'DISCHARGE').totalCount
                          ? getDocumentInfoObj(dataRes, 'DISCHARGE').totalCount
                          : 0
                      }`}
                    {/* {index === 2 &&
                      `${
                        getTaskTypeInfoObj(dataRes, 'EVALUATION')?.completedCount
                          ? getTaskTypeInfoObj(dataRes, 'EVALUATION')?.completedCount
                          : 0
                      }/${
                        getTaskTypeInfoObj(dataRes, 'EVALUATION')?.totalCount
                          ? getTaskTypeInfoObj(dataRes, 'EVALUATION')?.totalCount
                          : 0
                      }`} */}
                  </Typography>
                  {index === 1 && (
                    <Typography
                      sx={{ color: '#384250', marginLeft: '5px' }}
                      variant="caption"
                      noWrap
                    >
                      Discharge
                    </Typography>
                  )}
                  {/* {index === 2 && (
                    <Typography
                      sx={{ color: '#384250', marginLeft: '5px' }}
                      variant="caption"
                      noWrap
                    >
                      Evaluation task
                    </Typography>
                  )} */}
                  {index === 0 && (
                    <Typography
                      sx={{ color: '#384250', marginLeft: '5px', marginRight: '5px' }}
                      variant="caption"
                      noWrap
                    >
                      Group therapy
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                  {index === 0 && (
                    <>
                      <Typography
                        sx={{ color: '#9E9E9E' }}
                        variant="caption"
                        noWrap
                      >
                        {`${
                          getTherapyInfoObj(dataRes, 'GROUP_THERAPY')?.completedMinutes
                            ? getTherapyInfoObj(dataRes, 'GROUP_THERAPY')?.completedMinutes
                            : 0
                        }/${
                          getTherapyInfoObj(dataRes, 'GROUP_THERAPY')?.totalMinutes
                            ? getTherapyInfoObj(dataRes, 'GROUP_THERAPY')?.totalMinutes
                            : 0
                        }  mins`}
                      </Typography>
                    </>
                  )}
                  {/* {index === 2 && (
                    <>
                      <Typography
                        sx={{ color: '#9E9E9E' }}
                        variant="caption"
                        noWrap
                      >
                        {`${
                          getTaskTypeInfoObj(dataRes, 'EVALUATION')?.completedMinutes
                            ? getTaskTypeInfoObj(dataRes, 'EVALUATION')?.completedMinutes
                            : 0
                        }/${
                          getTaskTypeInfoObj(dataRes, 'EVALUATION')?.totalMinutes
                            ? getTaskTypeInfoObj(dataRes, 'EVALUATION')?.totalMinutes
                            : 0
                        }  mins`}
                      </Typography>
                    </>
                  )} */}
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  px: 0.8,
                  // py: 0.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {index === 0 && <TaskOverviewIcons iconType={'Evaluation'} />}
                  <Typography
                    sx={{ color: '#384250', marginLeft: '5px' }}
                    variant="caption"
                    noWrap
                  >
                    {index === 0 &&
                      `${
                        getTherapyInfoObj(dataRes, 'EVALUATION')?.completedCount
                          ? getTherapyInfoObj(dataRes, 'EVALUATION')?.completedCount
                          : 0
                      }/${
                        getTherapyInfoObj(dataRes, 'EVALUATION')?.totalCount
                          ? getTherapyInfoObj(dataRes, 'EVALUATION')?.totalCount
                          : 0
                      } `}
                  </Typography>
                  {index === 0 && (
                    <Typography
                      sx={{ color: '#384250', marginLeft: '5px' }}
                      variant="caption"
                      noWrap
                    >
                      Evaluations
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2, px: 0.5 }}>
                  <Typography
                    sx={{ color: '#9E9E9E' }}
                    variant="caption"
                    noWrap
                  >
                    {index === 0 &&
                      `${
                        getTherapyInfoObj(dataRes, 'EVALUATION')?.completedMinutes
                          ? getTherapyInfoObj(dataRes, 'EVALUATION')?.completedMinutes
                          : 0
                      }/${
                        getTherapyInfoObj(dataRes, 'EVALUATION')?.totalMinutes
                          ? getTherapyInfoObj(dataRes, 'EVALUATION')?.totalMinutes
                          : 0
                      } mins`}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  px: 0.8,
                  }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {index === 1 && <TaskOverviewIcons iconType={'Weekly Progress Notes'} />}
                  <Typography
                    sx={{ color: '#384250', marginLeft: '5px' }}
                    variant="caption"
                    noWrap
                  >
                    {index === 1 &&
                      `${
                        getDocumentInfoObj(dataRes, 'WEEKLY_PROGRESS').completedCount
                          ? getDocumentInfoObj(dataRes, 'WEEKLY_PROGRESS').completedCount
                          : 0
                      }/${
                        getDocumentInfoObj(dataRes, 'WEEKLY_PROGRESS').totalCount
                          ? getDocumentInfoObj(dataRes, 'WEEKLY_PROGRESS').totalCount
                          : 0
                      }`}
                  </Typography>
                  {index === 1 && (
                    <Typography
                      sx={{ color: '#384250', marginLeft: '5px' }}
                      variant="caption"
                      noWrap
                    >
                      Weekly Progress Notes
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  px: 0.8,
                  py: 0.5,
                }}
                // onClick={() => handleSingleTask(dataRes)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {index === 1 && <TaskOverviewIcons iconType={'Daily Session Note'} />}
                  <Typography
                    sx={{ color: '#384250', marginLeft: '5px' }}
                    variant="caption"
                    noWrap
                  >
                    {index === 1 &&
                      `${
                        getDocumentInfoObj(dataRes, 'SOAP_NOTE').completedCount
                          ? getDocumentInfoObj(dataRes, 'SOAP_NOTE').completedCount
                          : 0
                      }/${
                        getDocumentInfoObj(dataRes, 'SOAP_NOTE').totalCount
                          ? getDocumentInfoObj(dataRes, 'SOAP_NOTE').totalCount
                          : 0
                      }`}
                  </Typography>
                  {index === 1 && (
                    <Typography
                      sx={{ color: '#384250', marginLeft: '5px' }}
                      variant="caption"
                      noWrap
                    >
                      Soap Notes
                    </Typography>
                  )}
                </Box>
              </Box>
            </>
          }
        </Box>
      )}
    </Box>
  );
};
